// Library Imports
import * as React from "react";
import {
  Box,
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

// app setting imports
import {
  AuthenticationContext,
  ImagesContext,
} from "../../../Context";

// importing header helpers
import { getHeaders } from "../../../utils/HeaderHelper";
import { formatToUsDateFromUTC } from "../../../utils/DateHelper";
import { useParams } from "react-router-dom";
import {
  getSortedListByDate,
  getSortedListByNumber,
  getSortedListByString,
  setSortOrder,
} from "../../../shared/tables/TableHelper";
import AddHistoricalImmunization from "./AddHistoricalImmunization";
import CreateIcon from "@mui/icons-material/Create";
import { Delete } from "@mui/icons-material";
import { useFlash } from "../../../FlashContext";

interface Props {}

const PatientImmunizations: React.FC<Props> = (props: any) => {
  const { id: patientId } = useParams();
  // authentication context
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  // other states
  const [allImmunizations, setAllImmunizations] = React.useState<any>([]);
  const [linkedWithNdiis, setLinkedWithNdiis] = React.useState<boolean>(false);
  const [linkBtnLabel, setLinkBtnLabel] =
    React.useState<string>("Link to NDIIS");

  const [showHistoricalImmunization, setShowHistoricalImmunization] =
    React.useState<boolean>(false);
  const [selectedImmunization, setSelectedImmunization] = React.useState(null);

  React.useEffect(() => {
    getImmunizations();
  }, [patientId]);

  const getImmunizations = async () => {
    const response = await fetch(`/patient_immunizations/${patientId}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    });
    if (response.status === 404) {
      window.location.href = "/not-found";
      return;
    }
    const result = await response.json();
    if (result?.resource) {
      const immunizations = [
        ...(result?.resource?.historic?.map((item) => ({
          id: item.id || null,
          immunization: item.vaccine_name || null,
          date_received: item.immunization_date || null,
          due_date: null,
          type: "historic",
        })) || []),
        ...(result?.resource?.forecast?.map((item) => ({
          id: item.id || null,
          immunization: item.vaccine_type || null,
          date_received: null,
          due_date: item.recommended_date || null,
          type: "forecast",
        })) || []),
      ];

      setAllImmunizations(immunizations);

      setLinkedWithNdiis(result?.resource?.ndiis_linked);
    } else {
      alert("Something is wrong and template cannot be saved");
    }
  };

  const handleLinkToNdiis = () => {
    setLinkBtnLabel("Loading...");
    fetch(`/link_to_ndiis/${patientId}`, {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          alert(result.error);
        } else {
          if (result?.resource) {
            setLinkedWithNdiis(result?.resource?.ndiis_linked);
            if (result?.resource?.ndiis_linked) {
              showToast({
                title: "HIE Linked Successfully",
                text: "Linked with NDIIS",
                type: "success",
              });
              getImmunizations();
            } else {
              showToast({
                title: "Failed to Link with NDIIS",
                text: "Cannot link with NDIIS",
                type: "error",
              });
            }
          } else {
            alert("Something is wrong and template cannot be saved");
          }
          setLinkBtnLabel("Link to NDIIS");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const [sortObject, setSortObject] = React.useState<any>({
    field: "",
    direction: "",
  });

  React.useEffect(() => {
    if (allImmunizations) {
      sortList();
    }
  }, [sortObject]);

  const sortList = () => {
    const immunizationsList = getSortedAndSearchedList();
    setAllImmunizations(immunizationsList);
  };
  const images = React.useContext(ImagesContext);

  const openAddHistoricalImmunizationModal = () =>
    setShowHistoricalImmunization(true);
  const closeAddHistoricalImmunizationModal = () => {
    setSelectedImmunization(null);
    setShowHistoricalImmunization(false);
  };

  const getSortIcon = (column) => {
    return sortObject.field == column ? (
      <span className="sortIndicator">
        {sortObject.direction == "ascending" ? (
          <img
            src={images.sort_ascending_src}
            width="10"
            className="sort-icon"
            alt="Sort Asc"
          />
        ) : (
          <img
            src={images.sort_descending_src}
            width="10"
            className="sort-icon"
            alt="Sort Desc"
          />
        )}
      </span>
    ) : (
      <span className="sortIndicator">
        <img
          src={images.sort_plain_src}
          width="10"
          className="sort-icon"
          alt="Sort Asc"
        />
      </span>
    );
  };

  const getSortedAndSearchedList = () => {
    let imList = [...allImmunizations];
    imList.sort((a, b) => (a.id > b.id ? 1 : -1));
    switch (sortObject.field) {
      case "immunization":
        return getSortedListByString(imList, sortObject, "immunization");
      case "date_received":
        return getSortedListByDate(imList, sortObject, "date_received");
      case "due_date":
        return getSortedListByDate(imList, sortObject, "due_date");
      default:
        return getSortedListByNumber(imList, sortObject, "id");
    }
  };

  const setImmunizationForEdit = (immunization) => {
    setSelectedImmunization(immunization);
    openAddHistoricalImmunizationModal();
  };

  const deleteHistoricalImmunization = async (immunization) => {
    const url = `/delete_historical_immunization/${immunization.id}`;
    const method = "DELETE";

    try {
      const response = await fetch(url, {
        method,
        headers: getHeaders(authenticationSetting.csrfToken),
      });

      const result = await response.json();

      if (result.success) {
        showToast({
          title: "Immunization Deleted",
          text: "Immunization deleted successfully",
          type: "success",
        });
        getImmunizations();
      } else {
        showToast({
          title: "Failed to Delete Immunization",
          text: "Something went wrong",
          type: "error",
        });
      }
    } catch (error) {
      showToast({
        title: "Failed to Delete Immunization",
        text: error.message ?? "Failed to delete Immunization history",
        type: "error",
      });
    }
  };

  return (
    <Grid className="panel-container">
      <Grid item xs={12}>
        <Grid container className="panel-show-container">
          <Grid container className="panel-information-container">
            <Grid container direction="row" className="admin-header">
              <Grid item xs={12} className="box-header">
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  paddingX={3}
                  paddingY={1}
                  alignItems={"center"}
                  display={"flex"}
                >
                  <Grid item>
                    <h3>Immunizations</h3>
                  </Grid>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      columnGap: "20px",
                    }}
                  >
                    <Link
                      className="action-link row"
                      style={{ backgroundColor: "transparent" }}
                      onClick={openAddHistoricalImmunizationModal}
                    >
                      <img
                        src={images.add_icon}
                        alt="Add Charge"
                        className="action-icon-image"
                        style={{ width: 20, maxHeight: 20 }}
                      />
                      <span className="app-user-text">
                        Add Historical Immunization
                      </span>
                    </Link>
                    {!linkedWithNdiis && (
                      <Link
                        className="basic-button orange-btn"
                        style={{ width: "150px" }}
                        onClick={handleLinkToNdiis}
                      >
                        {linkBtnLabel}
                      </Link>
                    )}
                  </Box>
                </Stack>
              </Grid>
            </Grid>
            <Grid
              container
              className="medication-container grey-container pad-top-10"
            >
              <Grid item xs={12} className="medication-table-container">
                <TableContainer>
                  <Table>
                    <TableHead className="table-header-box">
                      <TableRow>
                        <TableCell
                          className="nowrap-header"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSortOrder(
                              "immunization",
                              sortObject.direction == "ascending"
                                ? "descending"
                                : "ascending",
                              setSortObject
                            );
                          }}
                        >
                          Immunization {getSortIcon("immunization")}
                        </TableCell>
                        <TableCell
                          className="nowrap-header"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSortOrder(
                              "date_received",
                              sortObject.direction == "ascending"
                                ? "descending"
                                : "ascending",
                              setSortObject
                            );
                          }}
                        >
                          Date Received {getSortIcon("date_received")}
                        </TableCell>
                        <TableCell
                          className="nowrap-header"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSortOrder(
                              "due_date",
                              sortObject.direction == "ascending"
                                ? "descending"
                                : "ascending",
                              setSortObject
                            );
                          }}
                        >
                          Recommended Date {getSortIcon("due_date")}
                        </TableCell>
                        <TableCell align="center" sx={{ width: "0px" }}>
                          Edit
                        </TableCell>
                        <TableCell align="center" sx={{ width: "0px" }}>
                          Delete
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allImmunizations?.map((immunization) => (
                        <TableRow
                          key={immunization.id}
                          sx={{ "& td, & th": { borderBottom: "none" } }}
                        >
                          <TableCell>{immunization.immunization}</TableCell>
                          <TableCell>
                            {!!immunization?.date_received
                              ? formatToUsDateFromUTC(
                                  immunization.date_received
                                )
                              : ""}
                          </TableCell>
                          <TableCell>
                            {!!immunization?.due_date
                              ? formatToUsDateFromUTC(immunization.due_date)
                              : ""}
                          </TableCell>
                          <TableCell align="center">
                            {immunization.type === "historic" && (
                              <CreateIcon
                                style={{
                                  display: "inline-block",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  setImmunizationForEdit(immunization)
                                }
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {immunization.type === "historic" && (
                              <Delete
                                style={{
                                  display: "inline-block",
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  deleteHistoricalImmunization(immunization);
                                }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {showHistoricalImmunization && (
        <AddHistoricalImmunization
          selectedImmunization={selectedImmunization}
          showHistoricalImmunization={showHistoricalImmunization}
          closeAddHistoricalImmunizationModal={
            closeAddHistoricalImmunizationModal
          }
          patientId={patientId}
          refreshImmunizations={getImmunizations}
        />
      )}
    </Grid>
  );
};

export default PatientImmunizations;
